/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect } from 'react';
import { Menu, X, ArrowLeft } from 'react-feather';
import { Button } from '@doar/components'; // ลบ Navbar ออกไป
import Logo from '../../components/logo';
import NotificationDropdown from '../../components/header/notification-dropdown';
import ProfileDropdown from '../../components/header/profile-dropdown';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { toggleSidebar, toggleBody } from '../../redux/slices/ui';
import AuthService from '../../services/auth.service';
import UserService from '../../services/user.service';
import Englishtext from '@doar/shared/Text/header/en.json';
import Thaitext from '@doar/shared/Text/header/th.json';
import {
    StyledHeader,
    StyledLogo,
    StyledNavbarWrap,
    StyledNavbarMenu,
    StyleNavbarRight,
    StyledNavbarElement,
    StyledNavbarHeader,
    StyledNavbarBody,
    StyledMenuBtn,
    StyledSidebarBtn,
    // อาจจะต้องเพิ่ม StyledMenuItem และ StyledNavList ใน style.ts ด้วย
    StyledNavLink, // เพิ่ม StyledNavLink สำหรับลิงก์
    StyledNavList, // เพิ่ม StyledNavList สำหรับ ul
} from './style';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface IProps {
    hasSidebar?: boolean;
    sidebarLayout?: 1 | 2;
}

const Header = ({ hasSidebar, sidebarLayout }: IProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const isLogin = AuthService.isLogin();
    const dispatch = useAppDispatch();
    const { sidebar, isBody } = useAppSelector((state) => state.ui);
    const [menuOpen, setMenuOpen] = useState(false);

    const sidebarHandler = useCallback(
        (_: any, isOpen?: 'open') => {
            dispatch(toggleSidebar({ isOpen }));
        },
        [dispatch]
    );

    const bodyHandler = useCallback(() => {
        dispatch(toggleBody());
        dispatch(toggleSidebar({ isOpen: 'open' }));
    }, [dispatch]);

    const menuHandler = useCallback(() => {
        setMenuOpen((prev) => !prev);
        // เมื่อเปิดเมนูและ sidebar ไม่ได้ถูกเปิดไว้ ให้เปิด sidebar ด้วย
        if (!menuOpen && !sidebar) {
            // ตรวจสอบว่าเมนูปิดอยู่ก่อนที่จะเปิด และ sidebar ไม่ได้ถูกเปิดอยู่
            sidebarHandler(undefined, 'open');
        }
    }, [menuOpen, sidebar, sidebarHandler]);

    useEffect(() => {
        // Cleanup function for initial state or when component unmounts
        return () => {
            // This part might be causing issues on initial load if sidebar is not supposed to be open by default
            // Consider removing or adjusting if sidebar state is managed elsewhere on first load
            // For now, keeping it as per original logic if it's meant to reset state on unmount
            // sidebarHandler(undefined, 'open');
            // bodyHandler();
        };
    }, []); // Empty dependency array means this runs once on mount and once on unmount

    // ✅ เพิ่มฟังก์ชัน currentLang() เพื่อตรวจสอบภาษาที่เลือกไว้
    const currentLang = () => {
        if (UserService.getCurrentLang() === 'th') {
            return Thaitext;
        }
        return Englishtext;
    };

    const myMenu = [
        { id: 0, label: t('header.home'), url: '/home' },
        { id: 1, label: t('header.graph'), url: '/graph' },
        { id: 2, label: t('header.track'), url: '/track' },
        {
            id: 3,
            label: t('header.hospAndClin'),
            url: '/hospitals-clinics-list',
        },
        {
            id: 4,
            label: t('header.makeApp'),
            url: '/appointment',
        },
        {
            id: 5,
            label: t('header.treatment'),
            url: '/treatment-records',
        },
        { id: 6, label: t('header.about'), url: '/about-us' },
        { id: 7, label: t('header.newsEvent'), url: '/news-and-event' },
    ];

    // ✅ เพิ่มการกรองเมนูเมื่อไม่ login
    const filteredMenu = isLogin
        ? myMenu
        : myMenu.filter((menu) => [6, 7].includes(menu.id));

    // Handler สำหรับการคลิกที่ลิงก์เมนูที่เราสร้างเอง

    const handleMenuItemClick = (menuItem: (typeof myMenu)[0]) => {
        const BASE_EXTERNAL_URL = 'https://web.techcaresystem.com'; // กำหนด Base URL

        if (menuItem.id === 6 || menuItem.id === 7) {
            // รวม Base URL กับส่วนท้ายของ URL เดิม
            const newUrl = `${BASE_EXTERNAL_URL}${menuItem.url}`;
            window.location.href = newUrl;
        } else {
            // สำหรับเมนูอื่นๆ ที่ยังคงใช้การ routing ภายในแอป
            window.location.href = menuItem.url;
        }
        setMenuOpen(false); // ปิดเมนูหลังจากคลิก
    };

    return (
        <>
            <StyledHeader>
                {hasSidebar && sidebarLayout === 1 && (
                    <>
                        <StyledMenuBtn
                            variant="texted"
                            onClick={menuHandler}
                            $hasSidebar={hasSidebar}
                            $sidebarOpen={sidebar}
                            $bodyOpen={isBody}
                            className="menu-btn"
                        >
                            <Menu size={20} strokeWidth="2.5px" />
                        </StyledMenuBtn>
                        <StyledSidebarBtn
                            variant="texted"
                            onClick={!isBody ? sidebarHandler : bodyHandler}
                            $sidebarOpen={sidebar}
                            $bodyOpen={isBody}
                            className="sidebar-btn"
                        >
                            <ArrowLeft size={20} strokeWidth="2.5px" />
                        </StyledSidebarBtn>
                    </>
                )}
                {hasSidebar && sidebarLayout === 2 && (
                    <>
                        <StyledMenuBtn
                            variant="texted"
                            onClick={menuHandler}
                            $hasSidebar={hasSidebar}
                            $sidebarOpen={!sidebar}
                        >
                            <Menu size={20} strokeWidth="2.5px" />
                        </StyledMenuBtn>
                        <StyledSidebarBtn
                            variant="texted"
                            onClick={sidebarHandler}
                            $sidebarOpen={!sidebar}
                        >
                            <ArrowLeft size={20} strokeWidth="2.5px" />
                        </StyledSidebarBtn>
                    </>
                )}
                {!hasSidebar && (
                    <StyledMenuBtn
                        variant="texted"
                        onClick={menuHandler}
                        $hasSidebar={hasSidebar}
                        $sidebarOpen={!sidebar}
                    >
                        <Menu size={20} strokeWidth="2.5px" />
                    </StyledMenuBtn>
                )}
                <StyledLogo>
                    {/* This is Logo */}
                    <Logo />
                </StyledLogo>
                <StyledNavbarWrap
                    $isOpen={menuOpen} // ส่ง $isOpen ไปที่ StyledNavbarWrap
                    onClick={menuHandler}
                    className="navbar-wrap"
                >
                    <StyledNavbarMenu
                        $isOpen={menuOpen} // ส่ง $isOpen ไปที่ StyledNavbarMenu
                        onClick={(e) => e.stopPropagation()}
                    >
                        <StyledNavbarHeader>{/* ... */}</StyledNavbarHeader>
                        <StyledNavbarBody $isOpen={menuOpen}>
                            {' '}
                            {/* <-- สำคัญ: เพิ่มตรงนี้ */}
                            <StyledNavList>
                                {filteredMenu.map((menuItem) => (
                                    <li key={menuItem.id}>
                                        <StyledNavLink
                                            href={menuItem.url}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                handleMenuItemClick(menuItem);
                                            }}
                                        >
                                            {menuItem.label}
                                        </StyledNavLink>
                                    </li>
                                ))}
                            </StyledNavList>
                        </StyledNavbarBody>
                    </StyledNavbarMenu>
                </StyledNavbarWrap>
                <StyleNavbarRight>
                    <StyledNavbarElement ml={['8px', '15px']}>
                        <NotificationDropdown />
                    </StyledNavbarElement>
                    <StyledNavbarElement ml={['15px', '20px', '30px']}>
                        {isLogin && <ProfileDropdown />}
                        {isLogin === false && (
                            <Button
                                color="primary"
                                shape="ellipse"
                                onClick={() => navigate('/signin')}
                            >
                                {currentLang().signin}
                            </Button>
                        )}
                    </StyledNavbarElement>
                </StyleNavbarRight>
            </StyledHeader>
        </>
    );
};

Header.defaultProps = {
    sidebarLayout: 1,
};

export default Header;
